import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX, ReactElement } from 'react';
import { Switch, Tooltip } from 'antd';
import { CaretRightOutlined } from '@ant-design/icons';

import { Button, Chip, Tag } from '../ds';
import type { MyPlanCardDto, MyPlanStageDto, MyPlanTaskDto } from '../../lib/api/openingTasks';
import './PlanGantt.css';
import {
  DEFAULT_SCALE,
  DOABLE_THIS_WEEK_LABEL,
  GANTT_LEGEND_STATUSES,
  doableThisWeekIds,
  GANTT_SCALES,
  GANTT_SCALE_LABEL,
  GANTT_STATUS_COLOR,
  GANTT_STATUS_LABEL,
  NO_FILTER,
  PX_PER_DAY,
  SIDE_FILTERS,
  SIDE_FILTER_LABEL,
  allExpanded,
  barRect,
  buildMarkers,
  buildRows,
  buildScaleHeader,
  carriedText,
  computeRange,
  dateCenterX,
  dateToX,
  effectiveExpanded,
  expandableCodes,
  groupTasksByStage,
  isExpandable,
  isFilterActive,
  isTaskDone,
  isUkTask,
  normYmd,
  phaseColor,
  planRangeDates,
  scrollTargetLeft,
  sideCounts,
  stageStateText,
  stageTipLines,
  taskSpan,
  taskTipLines,
  withAlpha,
  type DateRange,
  type GanttScale,
  type PlanFilter,
  type PlanMarker,
  type PlanRow,
  type SideFilter,
} from './plan-gantt-model';

/**
 * Диаграмма Ганта плана открытия для партнёра (вики, визард «Открытие точки», владелец 31.10.2026):
 * ТОЛЬКО ЧТЕНИЕ — ни перетаскивания, ни правки сроков. Вид — как в карточке открытия CRM
 * (`frontend/src/pages/opening/v2/GanttChart.tsx`): слева названия (этап → его задачи, этапы
 * сворачиваются, по умолчанию раскрыт текущий), справа шкала с горизонтальной прокруткой внутри блока.
 * Бары задач — цветом статуса (подпись всегда есть и словами: в колонке, подсказке и легенде),
 * вехи — ромб, просроченные — красная обводка, задачи УК приглушены и помечены «УК».
 * Вертикали: «Сегодня», «Аренда», «Тех. открытие», «Официальное открытие» (≈ — прогноз).
 * Вся арифметика дат и разметка — в `plan-gantt-model.ts` (покрыто тестами), здесь только вёрстка.
 */

const TIER1 = 22;
const TIER2 = 24;
const LANE_H = 16;

/** Значения CSS-переменных для inline-стилей. */
const cssVars = (v: Record<string, string | number>): CSSProperties => v as CSSProperties;

/** Подсказка на баре: первая строка — жирным, остальные обычным. */
function Tip({ lines, children }: { lines: string[]; children: ReactElement }): JSX.Element {
  return (
    <Tooltip
      mouseEnterDelay={0.1}
      trigger={['hover', 'focus']}
      title={
        <div className="fk-pg-tip">
          {lines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
}

// ───────────────────────── Бары ─────────────────────────

function StageBar({ stage, range, ppd, today, phaseLabel }: { stage: MyPlanStageDto; range: DateRange; ppd: number; today: string; phaseLabel: string | null }): JSX.Element | null {
  const rect = barRect(range, ppd, { start: stage.start, end: stage.end });
  if (!rect) return null;
  const color = phaseColor(stage.phaseCode);
  // Пройденная часть текущего этапа: от начала бара до конца сегодняшнего дня.
  const progress = Math.min(rect.width, Math.max(0, dateToX(range, today, ppd) + ppd - rect.left));
  const lines = stageTipLines(stage, phaseLabel);
  return (
    <Tip lines={lines}>
      <div
        className="fk-pg-sb"
        role="img"
        tabIndex={0}
        aria-label={lines.join('. ')}
        data-state={stage.state}
        data-est={stage.estimated}
        data-clip-r={rect.clippedRight}
        style={{ left: rect.left, width: rect.width, ...cssVars({ '--c': color, '--c-soft': withAlpha(color, 0.3), '--p': `${progress}px` }) }}
      />
    </Tip>
  );
}

function TaskBar({ task, range, ppd, stageLabel, doable }: { task: MyPlanTaskDto; range: DateRange; ppd: number; stageLabel: string; doable: boolean }): JSX.Element | null {
  const span = taskSpan(task);
  if (!span) return null;
  const lines = taskTipLines(task, stageLabel, doable);
  const common = {
    role: 'img' as const,
    tabIndex: 0,
    'aria-label': lines.join('. '),
    'data-st': task.ganttStatus,
    'data-uk': isUkTask(task),
    'data-doable': doable,
    'data-overdue': task.attention === 'overdue' && !isTaskDone(task),
  };
  const color = GANTT_STATUS_COLOR[task.ganttStatus] ?? GANTT_STATUS_COLOR.plan;
  if (task.isMilestone) {
    // Веха — точка на дне срока.
    if (span.end < range.start || span.end > range.end) return null;
    return (
      <Tip lines={lines}>
        <div className="fk-pg-ms" {...common} style={{ left: dateCenterX(range, span.end, ppd), ...cssVars({ '--c': color }) }} />
      </Tip>
    );
  }
  const rect = barRect(range, ppd, span);
  if (!rect) return null;
  return (
    <Tip lines={lines}>
      <div className="fk-pg-b" {...common} data-clip-r={rect.clippedRight} style={{ left: rect.left, width: rect.width, ...cssVars({ '--c': color }) }} />
    </Tip>
  );
}

// ───────────────────────── Строки ─────────────────────────

function StageRowView({
  row,
  range,
  ppd,
  trackW,
  today,
  phaseLabels,
  onToggle,
}: {
  row: Extract<PlanRow, { kind: 'stage' }>;
  range: DateRange;
  ppd: number;
  trackW: number;
  today: string;
  phaseLabels: Map<string, string>;
  onToggle: (code: string) => void;
}): JSX.Element {
  const { group, open } = row;
  const st = group.stage;
  const label = (
    <>
      <span className="fk-pg-text" title={st.deptLabel ? `${st.label} · ${st.deptLabel}` : st.label}>
        <b className="fk-pg-sname">{st.label}</b>
        {st.deptLabel && <span className="fk-pg-dept"> · {st.deptLabel}</span>}
      </span>
      {st.tasksTotal > 0 && (
        <span className="fk-pg-cnt" title={`Выполнено задач: ${st.tasksDone} из ${st.tasksTotal}`}>
          {st.tasksDone}/{st.tasksTotal}
        </span>
      )}
    </>
  );
  return (
    <div className="fk-pg-row" data-kind="stage">
      <div className="fk-pg-name">
        {isExpandable(group) ? (
          <button type="button" className="fk-pg-toggle" aria-expanded={open} onClick={() => onToggle(st.code)}>
            <span className="fk-pg-caret" aria-hidden>
              <CaretRightOutlined />
            </span>
            {label}
          </button>
        ) : (
          <div className="fk-pg-plain">
            <span className="fk-pg-caret-gap" aria-hidden />
            {label}
          </div>
        )}
        <span className="fk-pg-st" data-st={st.state === 'done' ? 'done' : st.state === 'current' ? 'in_progress' : 'plan'}>
          {stageStateText(st)}
        </span>
      </div>
      <div className="fk-pg-track" style={{ width: trackW }}>
        <StageBar stage={st} range={range} ppd={ppd} today={today} phaseLabel={st.phaseCode ? (phaseLabels.get(st.phaseCode) ?? null) : null} />
      </div>
    </div>
  );
}

function TaskRowView({ row, range, ppd, trackW, doable }: { row: Extract<PlanRow, { kind: 'task' }>; range: DateRange; ppd: number; trackW: number; doable: boolean }): JSX.Element {
  const t = row.task;
  const uk = isUkTask(t);
  const overdue = t.attention === 'overdue' && !isTaskDone(t);
  const carried = carriedText(t);
  const noDates = taskSpan(t) === null;
  return (
    <div className="fk-pg-row" data-kind="task" data-uk={uk} data-overdue={overdue} data-doable={doable}>
      <div className="fk-pg-name">
        <div className="fk-pg-main">
          {uk && (
            <Tag color="indigo" title="Задачу выполняет управляющая компания" style={{ flex: 'none', fontSize: 10.5, lineHeight: '16px', padding: '0 5px' }}>
              УК
            </Tag>
          )}
          <span className="fk-pg-text" title={carried ? `${t.title} · ${carried}` : t.title}>
            <span className="fk-pg-title">{t.title}</span>
            {carried && <span className="fk-pg-note"> · {carried}</span>}
          </span>
          {doable && (
            <span className="fk-pg-wk" title="Предшественники выполнены, начало — не позже конца этой недели">
              {DOABLE_THIS_WEEK_LABEL}
            </span>
          )}
        </div>
        <span className="fk-pg-st" data-st={t.ganttStatus} data-overdue={overdue}>
          {overdue ? 'Просрочено' : GANTT_STATUS_LABEL[t.ganttStatus]}
        </span>
      </div>
      <div className="fk-pg-track" style={{ width: trackW }}>
        {noDates ? <span className="fk-pg-nodate">без срока</span> : <TaskBar task={t} range={range} ppd={ppd} stageLabel={row.stage.label} doable={doable} />}
      </div>
    </div>
  );
}

// ───────────────────────── Шапка и вертикали ─────────────────────────

const MARKER_STYLE: Record<PlanMarker['kind'], { dash: boolean; diamond: 'solid' | 'hollow' | 'none' }> = {
  today: { dash: true, diamond: 'none' },
  lease: { dash: true, diamond: 'none' },
  tech: { dash: false, diamond: 'solid' },
  official: { dash: false, diamond: 'hollow' },
};

function markerDiamond(m: PlanMarker): 'solid' | 'hollow' | 'none' {
  const d = MARKER_STYLE[m.kind].diamond;
  return d === 'solid' && m.estimated ? 'hollow' : d;
}

// ───────────────────────── Панель, легенда ─────────────────────────

function Legend({ phases }: { phases: Array<{ code: string; label: string }> }): JSX.Element {
  // На телефоне легенда свёрнута (она длинная и съела бы экран), на компьютере раскрыта.
  const [open, setOpen] = useState(() => !(typeof window !== 'undefined' && window.matchMedia?.('(max-width: 640px)').matches));
  return (
    <details className="fk-pg-lgbox" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Обозначения</summary>
      <div className="fk-pg-legend">
        <span className="fk-pg-lg-title">Задачи:</span>
        {GANTT_LEGEND_STATUSES.map((s) => (
          <span key={s} className="fk-pg-lg">
            <i className="fk-pg-sw" data-st={s} style={cssVars({ '--c': GANTT_STATUS_COLOR[s] })} />
            {GANTT_STATUS_LABEL[s]}
          </span>
        ))}
        <span className="fk-pg-lg">
          <i className="fk-pg-sw" data-kind="overdue" />
          Просрочено
        </span>
        <span className="fk-pg-lg">
          <i className="fk-pg-sw" data-kind="doable" />
          {DOABLE_THIS_WEEK_LABEL}
        </span>
        <span className="fk-pg-lg">
          <i className="fk-pg-sw" data-kind="ms" />
          Веха
        </span>
        <span className="fk-pg-lg">
          <i className="fk-pg-sw" data-kind="uk" style={cssVars({ '--c': GANTT_STATUS_COLOR.in_progress })} />
          Задача УК (приглушена)
        </span>
        {phases.length > 0 && <span className="fk-pg-lg-title">Этапы по фазам:</span>}
        {phases.map((p) => (
          <span key={p.code} className="fk-pg-lg">
            <i className="fk-pg-sw" data-kind="phase" style={cssVars({ '--c': phaseColor(p.code) })} />
            {p.label}
          </span>
        ))}
        <span className="fk-pg-lg">
          <i className="fk-pg-sw" data-kind="est" style={cssVars({ '--c': '#8B92A8', '--c-soft': withAlpha('#8B92A8', 0.3) })} />
          Штриховка — прогноз
        </span>
      </div>
    </details>
  );
}

// ───────────────────────── Компонент ─────────────────────────

export function PlanGantt({ card }: { card: MyPlanCardDto }): JSX.Element {
  const [scale, setScale] = useState<GanttScale>(DEFAULT_SCALE);
  const [filter, setFilter] = useState<PlanFilter>(NO_FILTER);
  // Выбор человека (клик по этапу, «Развернуть все»); null — раскрытие по умолчанию.
  const [manual, setManual] = useState<Set<string> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const ppd = PX_PER_DAY[scale];
  const today = normYmd(card.today) ?? card.today;
  const filterActive = isFilterActive(filter);

  const doable = useMemo(() => doableThisWeekIds(card.tasks, today), [card.tasks, today]);
  const groups = useMemo(() => groupTasksByStage(card.stages, card.tasks, filter, today), [card.stages, card.tasks, today, filter]);
  const expanded = useMemo(() => effectiveExpanded(groups, manual, filterActive), [groups, manual, filterActive]);
  const rows = useMemo(() => buildRows(groups, expanded), [groups, expanded]);
  const counts = useMemo(() => sideCounts(card.tasks, filter.onlyOpen), [card.tasks, filter.onlyOpen]);
  const totalTasks = useMemo(() => sideCounts(card.tasks, false).all, [card.tasks]);
  const shownTasks = useMemo(() => groups.reduce((n, g) => n + g.tasks.length, 0), [groups]);
  const phaseLabels = useMemo(() => new Map(card.phases.map((p) => [p.code, p.label])), [card.phases]);
  const usedPhases = useMemo(() => {
    const used = new Set(card.stages.map((s) => s.phaseCode));
    return card.phases.filter((p) => used.has(p.code));
  }, [card.phases, card.stages]);

  // Шкала. Диапазон не зависит от фильтра: иначе при каждом клике по чипу сетка «ездила» бы.
  const range = useMemo(() => computeRange(planRangeDates(card), scale, today), [card, scale, today]);
  const trackW = range.days * ppd;
  const header = useMemo(() => buildScaleHeader(range, scale, today), [range, scale, today]);
  const markers = useMemo(() => buildMarkers(card, range, ppd), [card, range, ppd]);
  const lanes = Math.max(1, ...markers.map((m) => m.lane + 1));
  const headH = TIER1 + TIER2 + lanes * LANE_H + 6;

  // ───────── «Сегодня» на виду при открытии и смене масштаба ─────────
  const todayX = dateCenterX(range, today, ppd);
  const todayXRef = useRef(todayX);
  useLayoutEffect(() => {
    todayXRef.current = todayX;
  });
  const hasChart = groups.length > 0;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const apply = (): boolean => {
      // Вкладка скрыта (display: none) — ширины нет; прокрутку доделает ResizeObserver, когда блок покажут.
      if (el.clientWidth === 0 || !Number.isFinite(todayXRef.current)) return false;
      const corner = el.querySelector<HTMLElement>('.fk-pg-corner');
      el.scrollLeft = scrollTargetLeft(todayXRef.current, el.clientWidth - (corner?.offsetWidth ?? 0));
      return true;
    };
    let pending = !apply();
    if (!pending || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (pending && apply()) {
        pending = false;
        ro.disconnect();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [scale, hasChart]);

  // ───────── Действия ─────────
  const toggleStage = (code: string) => {
    const next = new Set(expanded);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setManual(next);
  };
  const everythingOpen = allExpanded(groups, expanded);
  const hasExpandable = expandableCodes(groups).length > 0;
  const toggleAll = () => setManual(everythingOpen ? new Set() : new Set(expandableCodes(groups)));
  const setSide = (side: SideFilter) => {
    setFilter((f) => ({ ...f, side }));
    setManual(null);
  };
  const setOnlyOpen = (onlyOpen: boolean) => {
    setFilter((f) => ({ ...f, onlyOpen }));
    setManual(null);
  };
  const resetFilter = () => {
    setFilter(NO_FILTER);
    setManual(null);
  };

  if (card.stages.length === 0 && card.tasks.length === 0) {
    return (
      <div className="fk-pg">
        <div className="fk-pg-empty">План открытия пока не составлен — диаграмма появится, когда куратор добавит этапы и задачи.</div>
      </div>
    );
  }

  return (
    <div className="fk-pg">
      {card.paused && (
        <div className="fk-pg-pause" role="status">
          Открытие на паузе — сроки стоят
        </div>
      )}

      <div className="fk-pg-bar">
        <div className="fk-pg-group" role="group" aria-label="Масштаб шкалы">
          {GANTT_SCALES.map((s) => (
            <Chip key={s} active={scale === s} onClick={() => setScale(s)}>
              {GANTT_SCALE_LABEL[s]}
            </Chip>
          ))}
        </div>
        <span className="fk-pg-spacer" />
        <Button variant="ghost" size="sm" onClick={toggleAll} disabled={!hasExpandable}>
          {everythingOpen ? 'Свернуть все' : 'Развернуть все'}
        </Button>
      </div>

      <div className="fk-pg-bar">
        <div className="fk-pg-group" role="group" aria-label="Какие задачи показывать">
          {SIDE_FILTERS.map((s) => (
            <Chip
              key={s}
              active={filter.side === s}
              count={counts[s]}
              onClick={() => setSide(s)}
              title={s === 'partner' ? 'Задачи партнёра, в том числе общие с управляющей компанией' : s === 'uk' ? 'Задачи, которые выполняет управляющая компания' : undefined}
            >
              {SIDE_FILTER_LABEL[s]}
            </Chip>
          ))}
        </div>
        <label className="fk-pg-switch">
          <Switch size="small" checked={filter.onlyOpen} onChange={setOnlyOpen} />
          Только открытые
        </label>
        {filterActive && (
          <span className="fk-pg-count">
            Показано {shownTasks} из {totalTasks}
          </span>
        )}
      </div>

      <Legend phases={usedPhases} />

      {groups.length === 0 ? (
        <div className="fk-pg-empty" role="status">
          <span>Под выбранный фильтр задач нет. Сбросьте фильтр, чтобы увидеть все задачи плана.</span>
          <Button variant="ghost" size="sm" onClick={resetFilter}>
            Сбросить фильтр
          </Button>
        </div>
      ) : (
        <div className="fk-pg-scroll" ref={scrollRef} role="group" aria-label="Диаграмма Ганта плана открытия">
          <div className="fk-pg-canvas" style={{ width: `calc(var(--pg-left) + ${trackW}px)` }}>
            {/* Шапка: месяцы / недели / дни и подписи вертикалей */}
            <div className="fk-pg-head" style={{ height: headH }}>
              <div className="fk-pg-corner">
                <span>Этап · задачи</span>
              </div>
              <div className="fk-pg-htl" style={{ width: trackW }}>
                {header.top.map((c, i) => (
                  <div key={c.key} className="fk-pg-t1" data-first={i === 0} style={{ left: c.x, width: c.w }}>
                    <span style={{ maxWidth: Math.max(0, c.w - 12) }}>{c.text}</span>
                  </div>
                ))}
                {header.bottom.map((c) => (
                  <div key={c.key} className="fk-pg-t2" data-weekend={c.weekend ?? false} data-today={c.today ?? false} style={{ left: c.x, width: c.w }}>
                    {c.text}
                  </div>
                ))}
                {markers.map((m) => {
                  const top = TIER1 + TIER2 + 4 + m.lane * LANE_H;
                  return <i key={`l:${m.kind}`} className="fk-pg-mk" data-k={m.kind} data-dash={MARKER_STYLE[m.kind].dash} data-faded={m.estimated} style={{ left: m.x, top, height: headH - top }} />;
                })}
                {markers.map((m) => (
                  <span
                    key={`t:${m.kind}`}
                    className="fk-pg-mk-label"
                    data-k={m.kind}
                    data-faded={m.estimated}
                    data-diamond={markerDiamond(m)}
                    style={{ left: m.x, top: TIER1 + TIER2 + 4 + m.lane * LANE_H }}
                  >
                    <i aria-hidden />
                    {m.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="fk-pg-body">
              {/* Сетка под строками */}
              <div className="fk-pg-under" style={{ width: trackW }} aria-hidden>
                {scale === 'day' && header.bottom.filter((c) => c.weekend).map((c) => <i key={`we${c.key}`} className="fk-pg-we" style={{ left: c.x, width: c.w }} />)}
                {header.bottom.map((c, i) =>
                  i === 0 ? null : <i key={`gl${c.key}`} className="fk-pg-gl" data-strong={scale !== 'day' || i % 7 === 0} style={{ left: c.x }} />,
                )}
              </div>

              {rows.map((r) =>
                r.kind === 'stage' ? (
                  <StageRowView key={r.key} row={r} range={range} ppd={ppd} trackW={trackW} today={today} phaseLabels={phaseLabels} onToggle={toggleStage} />
                ) : (
                  <TaskRowView key={r.key} row={r} range={range} ppd={ppd} trackW={trackW} doable={doable.has(r.task.id)} />
                ),
              )}

              {/* Вертикали поверх строк */}
              <div className="fk-pg-over" style={{ width: trackW }} aria-hidden>
                {markers.map((m) => (
                  <i key={m.kind} className="fk-pg-vl" data-k={m.kind} data-dash={MARKER_STYLE[m.kind].dash} data-faded={m.estimated} style={{ left: m.x }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
